
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UserMembership {
  id: string;
  user_id: string;
  membership_id: string;
  sessions_remaining: number | null;
  sessions_used: number;
  purchase_date: string;
  expires_at: string | null;
  is_active: boolean;
  membership: {
    id: string;
    name: string;
    description: string | null;
    membership_type: string;
    session_count: number | null;
    price: number;
    currency: string;
    provider_id: string;
  };
  // Optional properties for subscription memberships
  isSubscription?: boolean;
  subscriptionStatus?: string;
}

export const useUserMemberships = (providerId?: string, serviceId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-memberships', user?.id, providerId, serviceId],
    queryFn: async () => {
      if (!user) return [];
      
      console.log('Fetching user memberships for user:', user.id, 'provider:', providerId, 'service:', serviceId);
      
      // Build the base query
      let query = supabase
        .from('user_memberships')
        .select(`
          *,
          membership:memberships!inner(
            id,
            name,
            description,
            membership_type,
            session_count,
            price,
            currency,
            provider_id
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      // If providerId is specified, filter by it using the inner join
      if (providerId) {
        query = query.eq('membership.provider_id', providerId);
      }

      const { data: userMemberships, error } = await query;

      if (error) {
        console.error('Error fetching user memberships:', error);
        throw error;
      }

      console.log('User memberships found:', userMemberships);

      if (!userMemberships || userMemberships.length === 0) {
        return [];
      }

      // Filter out memberships with no remaining sessions or expired subscriptions
      const activeMemberships = userMemberships.filter(membership => {
        // For session-based memberships, check if there are sessions remaining
        if (membership.membership.membership_type === 'pack_of_sessions') {
          return membership.sessions_remaining && membership.sessions_remaining > 0;
        }
        
        // For subscription memberships, check if not expired
        if (membership.membership.membership_type === 'monthly_subscription') {
          return !membership.expires_at || new Date(membership.expires_at) > new Date();
        }
        
        return true; // For other types, assume active if the record is active
      });

      console.log('Active memberships with credits:', activeMemberships);

      // If we have a specific service, filter memberships that include this service
      if (serviceId) {
        const validMemberships = [];
        
        for (const userMembership of activeMemberships) {
          const { data: membershipServices, error: servicesError } = await supabase
            .from('membership_services')
            .select('service_id')
            .eq('membership_id', userMembership.membership_id)
            .eq('service_id', serviceId);

          if (servicesError) {
            console.error('Error checking membership services:', servicesError);
            continue;
          }

          // If this membership includes the service, add it to valid memberships
          if (membershipServices && membershipServices.length > 0) {
            validMemberships.push(userMembership);
          }
        }

        console.log('Valid memberships for service:', validMemberships);
        return validMemberships as UserMembership[];
      }

      return activeMemberships as UserMembership[];
    },
    enabled: !!user,
  });
};
